/**
 * 管理后台 · 房间管理。
 * 替代老系统 admin/ 下的 man_room*.jsp 一组页面与 RoomAction。
 */

import { useCallback, useEffect, useState } from 'react'

import { adminApi, type RoomRow } from '../../lib/adminApi'
import { formatPerm, Perm } from '../../lib/protocol'
import { PermEditor } from './PermEditor'

interface Props {
  token: string
  onError: (msg: string) => void
  onNotice: (msg: string) => void
}

export function RoomsPanel({ token, onError, onNotice }: Props) {
  const [rows, setRows] = useState<RoomRow[]>([])
  const [total, setTotal] = useState(0)
  const [keyword, setKeyword] = useState('')
  const [offset, setOffset] = useState(0)
  const [busy, setBusy] = useState(false)
  const [editing, setEditing] = useState<RoomRow | null>(null)
  const [creating, setCreating] = useState(false)
  const limit = 20

  const load = useCallback(async () => {
    setBusy(true)
    try {
      const p = await adminApi.listRooms(token, keyword, limit, offset)
      setRows(p.items ?? [])
      setTotal(p.total)
    } catch (e) {
      onError(e instanceof Error ? e.message : '加载失败')
    } finally {
      setBusy(false)
    }
  }, [token, keyword, offset, onError])

  useEffect(() => {
    void load()
  }, [load])

  const remove = async (r: RoomRow) => {
    if (!confirm(`确定删除房间「${r.name}」（${r.id}）吗？\n录像与日志会保留。`)) return
    try {
      await adminApi.deleteRoom(token, r.id)
      onNotice('房间已删除')
      void load()
    } catch (e) {
      onError(e instanceof Error ? e.message : '删除失败')
    }
  }

  return (
    <div className="adm-panel">
      <div className="adm-bar">
        <input
          placeholder="搜索房间名 / 分组 / 房间号"
          value={keyword}
          onChange={(e) => {
            setKeyword(e.target.value)
            setOffset(0)
          }}
        />
        <button className="primary" onClick={() => setCreating(true)}>
          新建房间
        </button>
        <span className="adm-count">共 {total} 个房间</span>
      </div>

      <div className="adm-table-wrap">
        <table className="adm-table">
          <thead>
            <tr>
              <th>房间号</th>
              <th>名称</th>
              <th>分组</th>
              <th>在线</th>
              <th>容量</th>
              <th>状态</th>
              <th>房主</th>
              <th>默认权限</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id}>
                <td className="mono">{r.id}</td>
                <td>{r.name || '—'}</td>
                <td>{r.group || '—'}</td>
                <td>
                  {r.online > 0 ? (
                    <span className="live-dot">{r.online} 人在线</span>
                  ) : (
                    <span className="dim">0</span>
                  )}
                </td>
                <td>{r.maxUsers}</td>
                <td>
                  {r.open ? <em className="tag tag-ok">开放</em> : <em className="tag">关闭</em>}
                  {r.hasPass && <em className="tag">有密码</em>}
                </td>
                <td>{r.master || '—'}</td>
                <td className="mono dim" title="老库字母串形式">
                  {formatPerm(r.defaultProp) || '—'}
                </td>
                <td className="adm-ops">
                  <button className="mini" onClick={() => setEditing(r)}>
                    编辑
                  </button>
                  <button className="mini danger" onClick={() => void remove(r)}>
                    删除
                  </button>
                </td>
              </tr>
            ))}
            {rows.length === 0 && !busy && (
              <tr>
                <td colSpan={9} className="adm-empty">
                  没有匹配的房间
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Pager total={total} limit={limit} offset={offset} onChange={setOffset} />

      {(editing || creating) && (
        <RoomDialog
          token={token}
          room={editing}
          onClose={() => {
            setEditing(null)
            setCreating(false)
          }}
          onSaved={() => {
            setEditing(null)
            setCreating(false)
            onNotice('已保存')
            void load()
          }}
          onError={onError}
        />
      )}
    </div>
  )
}

/** 分页控件。 */
export function Pager({
  total,
  limit,
  offset,
  onChange,
}: {
  total: number
  limit: number
  offset: number
  onChange: (n: number) => void
}) {
  const page = Math.floor(offset / limit) + 1
  const pages = Math.max(1, Math.ceil(total / limit))
  if (pages <= 1) return null
  return (
    <div className="adm-pager">
      <button disabled={offset <= 0} onClick={() => onChange(Math.max(0, offset - limit))}>
        上一页
      </button>
      <span>
        {page} / {pages}
      </span>
      <button disabled={page >= pages} onClick={() => onChange(offset + limit)}>
        下一页
      </button>
    </div>
  )
}

/** 房间新建/编辑对话框。 */
function RoomDialog({
  token,
  room,
  onClose,
  onSaved,
  onError,
}: {
  token: string
  room: RoomRow | null
  onClose: () => void
  onSaved: () => void
  onError: (m: string) => void
}) {
  const isNew = !room
  const [id, setId] = useState(String(room?.id ?? ''))
  const [name, setName] = useState(room?.name ?? '')
  const [group, setGroup] = useState(room?.group ?? '')
  const [maxUsers, setMaxUsers] = useState(String(room?.maxUsers ?? 50))
  const [open, setOpen] = useState(room?.open ?? true)
  const [notice, setNotice] = useState(room?.notice ?? '')
  const [master, setMaster] = useState(room?.master ?? '')
  const [password, setPassword] = useState('')
  const [prop, setProp] = useState(
    room?.defaultProp || (Perm.Read | Perm.Write | Perm.Speak | Perm.Listen | Perm.Whiteboard),
  )
  const [busy, setBusy] = useState(false)

  const save = async () => {
    setBusy(true)
    try {
      if (isNew) {
        const n = Number(id)
        if (!Number.isInteger(n) || n <= 0) {
          onError('房间号必须是正整数')
          return
        }
        await adminApi.createRoom(token, {
          id: n,
          name,
          group,
          maxUsers: Number(maxUsers) || 50,
          open,
          notice,
          master,
          defaultProp: prop,
          ...(password ? { password } : {}),
        })
      } else {
        await adminApi.updateRoom(token, room!.id, {
          name,
          group,
          maxUsers: Number(maxUsers) || 50,
          open,
          notice,
          master,
          defaultProp: prop,
          // 密码留空表示不修改，而不是清空 —— 这是最容易误操作的一处。
          ...(password ? { password } : {}),
        })
      }
      onSaved()
    } catch (e) {
      onError(e instanceof Error ? e.message : '保存失败')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="dlg-mask" onClick={onClose}>
      <div className="dlg" onClick={(e) => e.stopPropagation()}>
        <h3>{isNew ? '新建房间' : `编辑房间 ${room!.id}`}</h3>

        <div className="dlg-grid">
          {isNew && (
            <label>
              <span>房间号</span>
              <input value={id} onChange={(e) => setId(e.target.value)} inputMode="numeric" />
            </label>
          )}
          <label>
            <span>房间名称</span>
            <input value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label>
            <span>分组</span>
            <input value={group} onChange={(e) => setGroup(e.target.value)} />
          </label>
          <label>
            <span>人数上限</span>
            <input value={maxUsers} onChange={(e) => setMaxUsers(e.target.value)} inputMode="numeric" />
          </label>
          <label>
            <span>房主账号</span>
            <input value={master} onChange={(e) => setMaster(e.target.value)} />
          </label>
          <label>
            <span>进房密码</span>
            <input
              type="password"
              value={password}
              placeholder={isNew ? '留空表示不设密码' : '留空表示不修改'}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          <label className="span2">
            <span>房间公告</span>
            <textarea value={notice} rows={2} onChange={(e) => setNotice(e.target.value)} />
          </label>
          <label className="span2 inline">
            <input type="checkbox" checked={open} onChange={(e) => setOpen(e.target.checked)} />
            <span>开放进入</span>
          </label>
        </div>

        <div className="dlg-section">
          <h4>新进用户的默认权限</h4>
          <PermEditor value={prop} onChange={setProp} />
        </div>

        <div className="dlg-foot">
          <button onClick={onClose}>取消</button>
          <button className="primary" disabled={busy} onClick={() => void save()}>
            {busy ? '保存中…' : '保存'}
          </button>
        </div>
      </div>
    </div>
  )
}
